import LoadingButton from "@mui/lab/LoadingButton";
import { Box, TextField, Typography, Button } from "@mui/material";
import Paper from "@mui/material/Paper";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PlaceAutoComplete from "../../third_parties/map/placeAutoComplete";
import { useContext, useEffect, useState } from "react";
import { APIProvider, Map, Marker, useApiIsLoaded } from "@vis.gl/react-google-maps";
import AlertContext from "../../context/alert/alertContext";
import DestinationContext from "../../context/destination/destinationContext";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const AddDestinationForm = () => {
    const isLoaded = useApiIsLoaded();
    const [isCreating, setIsCreating] = useState(false);
    const [isNameMissing, setIsNameMissing] = useState(false);
    const [, setIsPlaceMissing] = useState(false);

    const destinationContext = useContext(DestinationContext);
    const {
        addDestination,
        addDestinationComplete,
        destinationAdded,
        getDestinations,
    } = destinationContext;

    const alertContext = useContext(AlertContext);
    const { setAlert } = alertContext;

    useEffect(() => {
        if (isCreating && destinationAdded) {
            setIsCreating(false);
            setAlert("Successfully posted", "success");
            addDestinationComplete();
            getDestinations();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCreating, destinationAdded, addDestinationComplete]);

    const onSubmit = (e) => {
        if (destination.name === "") {
            setIsNameMissing(true);
            setAlert("Please write a name", "error");
            return;
        }
        if (destination.placeId === "") {
            setIsPlaceMissing(true);
            setAlert("Please choose a place", "error");
            return;
        }
        e.preventDefault();
        setIsCreating(true);
        addDestination(destination);
    };

    const [geo, setGeo] = useState({ lat: -33.860664, lng: 151.208138 });
    const [placeName, setPlaceName] = useState("");

    const [destination, setDestination] = useState({
        name: "",
        description: "",
        placeId: "",
    });

    const [file, setFile] = useState(null);

    const onSelectFile = (e) => {
        setDestination({
            ...destination,
            [e.target.name]: e.target.files[0],
        });
        setFile(e.target.files[0]);
    };

    return (
        <Paper variant="outlined" sx={{ p: 4, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Add a Destination
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <TextField
                    fullWidth
                    id="review-name"
                    label="Destination name"
                    name="name"
                    variant="outlined"
                    size="small"
                    onChange={(e) => {
                        setDestination({
                            ...destination,
                            [e.target.name]: e.target.value,
                        });
                    }}
                    value={destination.name ?? ""}
                    required
                    error={isNameMissing && destination.name === ""}
                />

                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    id="review-description"
                    label="Description"
                    name="description"
                    placeholder="Tell travelers about this destination..."
                    variant="outlined"
                    size="small"
                    onChange={(e) => {
                        setDestination({
                            ...destination,
                            [e.target.name]: e.target.value,
                        });
                    }}
                    value={destination.description ?? ""}
                />

                <PlaceAutoComplete
                    label="Search for a place"
                    name="place"
                    dataModel={destination}
                    setDataModel={setDestination}
                    placeName={placeName}
                    setPlaceName={setPlaceName}
                    geo={geo}
                    setGeo={setGeo}
                    isLoaded={isLoaded}
                />

                <Box sx={{ height: 280, width: '100%', borderRadius: 2, overflow: 'hidden' }}>
                    <Map
                        defaultZoom={13}
                        defaultCenter={geo}
                        center={geo}
                    >
                        {placeName ? <Marker position={geo} /> : <></>}
                    </Map>
                </Box>

                {/* Cover photo upload */}
                <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        Cover photo
                    </Typography>
                    <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        sx={{
                            textTransform: 'none',
                            borderStyle: 'dashed',
                            color: 'text.secondary',
                            py: 1.5,
                            width: '100%',
                            justifyContent: 'center',
                        }}
                    >
                        {file ? file.name : 'Upload a cover photo'}
                        <input
                            type="file"
                            accept="image/*"
                            name="cover_photos"
                            hidden
                            onChange={onSelectFile}
                        />
                    </Button>
                    {file && (
                        <Box
                            component="img"
                            src={URL.createObjectURL(file)}
                            sx={{
                                mt: 1.5,
                                width: '100%',
                                height: 160,
                                objectFit: 'cover',
                                borderRadius: 2,
                            }}
                        />
                    )}
                </Box>

                <LoadingButton
                    variant="contained"
                    type="submit"
                    onClick={onSubmit}
                    loading={isCreating}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 1.2,
                        borderRadius: 2,
                    }}
                >
                    Create Destination
                </LoadingButton>
            </Box>
        </Paper>
    );
};

const AddDestinationComposer = () => {
    return (
        <APIProvider apiKey={API_KEY} version="weekly">
            <AddDestinationForm />
        </APIProvider>
    );
};

export default AddDestinationComposer;
